/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import styles from './masthead.scss';
import StickyHeader from '@carbon/ibmdotcom-utilities/es/utilities/StickyHeader/StickyHeader.js';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Masthead.
 *
 * @element c4d-masthead
 * @csspart l0 - The Masthead L0. Usage: `c4d-masthead::part(l0)`
 * @csspart header - The header. Usage: `c4d-masthead::part(header)`
 * @csspart header-search - The header search. Usage: `c4d-masthead::part(header-search)`
 * @csspart header-nav-container - The header nav container. Usage: `c4d-masthead::part(header-nav-container)`
 * @slot brand - The left hand area.
 * @slot nav - The nav content.
 * @slot profile - The right hand area.
 */
@customElement(`${c4dPrefix}-masthead`)
class C4DMasthead extends StableSelectorMixin(LitElement) {
  firstUpdated() {
    StickyHeader.global.masthead = this;
  }

  /**
   * Re-initializes masthead component with StickyHeader class in case of L1 addition/removal.
   */
  handleL1Change({ target }) {
    const L1Navs = (target as HTMLSlotElement)
      .assignedElements()
      .filter(
        (element) =>
          element.tagName.toLowerCase() === `${c4dPrefix}-masthead-l1`
      );

    if (L1Navs.length) {
      StickyHeader.global.masthead = this;
    }
  }

  render() {
    return html`
      <div part="l0" class="${prefix}--masthead__l0">
        <div part="header" class="${prefix}--header">
          <slot name="brand"></slot>
          <div part="header-search" class="${prefix}--header__search">
            <div
              part="header-nav-container"
              class="${prefix}--header__nav-container">
              <slot></slot>
            </div>
            <slot name="search"></slot>
          </div>
          <slot name="profile"></slot>
        </div>
      </div>
      <slot name="masthead-l1" @slotchange=${this.handleL1Change}></slot>
    `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--masthead`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DMasthead;
